/**
 * /erp/dispatches/new — create a draft dispatch (Manager+ for PR-F).
 *
 * The form has two sections:
 *
 *   * Header — code (auto), driver / transport (master OR free-text temp),
 *     CRM links (deal / account / contact), notes.
 *   * Deliveries — zero or more rows; each can be external (recipient
 *     required) or internal (dest warehouse required), with line items
 *     pulled from products + raw_materials. Lines on external deliveries
 *     auto-fill `unit_price` from the product's `default_sell_price`
 *     (or the last-sold-to-this-account hint when present); internal
 *     deliveries hide the price column.
 *
 * Validation surfaces the API conflict messages verbatim (the backend
 * is the source of truth for the cross-field rules — Q12 contact-of-
 * account, Q15 line-price requirement, etc.) and adds a few clearly-
 * client-only checks (e.g. "pick a recipient before adding a line").
 *
 * On success we redirect to the detail page so the manager can either
 * confirm immediately or come back later to edit.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Loader2, Plus, Truck, X } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import {
  DispatchDeliveriesEditor,
  type DraftDelivery,
  makeEmptyDelivery,
} from "../components/DispatchDeliveriesEditor";
import {
  ApiError,
  type ErpDispatchCreate,
  accountsApi,
  contactsApi,
  dealsApi,
  erpApi,
  isErpDispatchConflict,
} from "../lib/api";
import {
  useDrivers,
  useOrders,
  useProducts,
  useRawMaterials,
  useTransports,
  useWarehouses,
} from "../lib/erp";
import { toast } from "../lib/toast";

interface NewDispatchSearch {
  order_id?: string | undefined;
}

export const Route = createFileRoute("/_app/erp/dispatches/new")({
  validateSearch: (search: Record<string, unknown>): NewDispatchSearch => ({
    order_id: typeof search.order_id === "string" ? search.order_id : undefined,
  }),
  component: NewDispatchPage,
});

function NewDispatchPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const driversQ = useDrivers();
  const transportsQ = useTransports();
  const warehousesQ = useWarehouses();
  const productsQ = useProducts();
  const rawsQ = useRawMaterials();
  const ordersQ = useOrders({ limit: 200 });
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });
  const dealsQ = useQuery({
    queryKey: ["deals", "dispatch-link-options"],
    queryFn: () => dealsApi.list({ limit: 200, open_only: true }),
  });

  const [code, setCode] = useState("");
  const [driverId, setDriverId] = useState<string>("");
  const [tempDriverName, setTempDriverName] = useState("");
  const [transportId, setTransportId] = useState<string>("");
  const [tempTransportName, setTempTransportName] = useState("");
  const [notes, setNotes] = useState("");
  const [dealId, setDealId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [contactId, setContactId] = useState("");
  const [deliveries, setDeliveries] = useState<DraftDelivery[]>([]);
  const [error, setError] = useState<string | null>(null);
  const seededFromOrder = useRef(false);

  const dispatchableOrders = (ordersQ.data?.items ?? []).filter(
    (o) => o.status === "ready_for_dispatch" || o.status === "partially_fulfilled",
  );

  // When arriving via "Add to dispatch" on an order, seed a single
  // external delivery pre-linked to that order. The OrderLink editor
  // then fetches the order and prefills its remaining lines + recipient.
  useEffect(() => {
    if (seededFromOrder.current) return;
    if (!search.order_id) return;
    seededFromOrder.current = true;
    const seeded = makeEmptyDelivery("external");
    seeded.order_id = search.order_id;
    setDeliveries([seeded]);
  }, [search.order_id]);

  const mutation = useMutation({
    mutationFn: (body: ErpDispatchCreate) => erpApi.dispatches.create(body),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
      toast.success(`Dispatch ${d.code} created.`);
      navigate({ to: "/erp/dispatches/$dispatchId", params: { dispatchId: d.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = err.detail;
        if (isErpDispatchConflict(detail)) {
          setError(detail.message);
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : "Could not create dispatch.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const body: ErpDispatchCreate = {};
    if (code.trim()) body.code = code.trim();
    if (driverId) body.driver_id = driverId;
    if (transportId) body.transport_id = transportId;
    if (tempDriverName.trim()) body.temp_driver_name = tempDriverName.trim();
    if (tempTransportName.trim()) body.temp_transport_name = tempTransportName.trim();
    if (notes.trim()) body.notes = notes.trim();
    if (dealId) body.deal_id = dealId;
    if (accountId) body.account_id = accountId;
    if (contactId) body.contact_id = contactId;
    if (deliveries.length > 0) {
      const issue = validateDraftDeliveries(deliveries);
      if (issue) {
        setError(issue);
        return;
      }
      body.deliveries = deliveries.map((d, idx) => ({
        kind: d.kind,
        source_warehouse_id: d.source_warehouse_id,
        ...(d.kind === "internal"
          ? { dest_warehouse_id: d.dest_warehouse_id || null }
          : {
              account_id: d.account_id || null,
              contact_id: d.contact_id || null,
              order_id: d.order_id || null,
              delivery_address: d.delivery_address.trim() ? d.delivery_address.trim() : null,
            }),
        notes: d.notes.trim() ? d.notes.trim() : null,
        position: idx,
        lines: d.lines.map((ln, li) => ({
          item_kind: ln.item_kind,
          ...(ln.item_kind === "product"
            ? { product_id: ln.item_id || null, raw_material_id: null }
            : ln.item_kind === "custom"
              ? {
                  product_id: null,
                  raw_material_id: null,
                  custom_name: ln.custom_name?.trim() || null,
                  custom_uom: ln.custom_uom?.trim() || null,
                }
              : { product_id: null, raw_material_id: ln.item_id || null }),
          qty: ln.qty.trim(),
          ...(d.kind === "external"
            ? { unit_price: ln.unit_price.trim() ? ln.unit_price.trim() : null }
            : { unit_price: null }),
          position: li,
        })),
      }));
    }

    mutation.mutate(body);
  };

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <Link
          to="/erp/dispatches"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> Dispatches
        </Link>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Truck className="size-5 opacity-70" aria-hidden />
          New dispatch
        </h1>
        <p className="text-sm opacity-60">
          Create a draft. You can edit deliveries freely until you confirm — at that point the line
          composition freezes and the source warehouses are debited.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4">
        <section className="space-y-3 rounded-xl border border-[var(--border)] p-4">
          <h2 className="text-sm font-semibold">Header</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank (DSP-…)"
                maxLength={32}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
              />
            </label>
            <div /> {/* spacer */}
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Driver</span>
              <select
                value={driverId}
                onChange={(e) => {
                  setDriverId(e.target.value);
                  if (e.target.value) setTempDriverName("");
                }}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">— pick a master —</option>
                {(driversQ.data?.items ?? [])
                  .filter((d) => d.is_active)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.code} — {d.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">…or one-off driver name</span>
              <input
                type="text"
                value={tempDriverName}
                onChange={(e) => {
                  setTempDriverName(e.target.value);
                  if (e.target.value) setDriverId("");
                }}
                placeholder="Temporary driver (won't auto-save to master)"
                maxLength={200}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Transport</span>
              <select
                value={transportId}
                onChange={(e) => {
                  setTransportId(e.target.value);
                  if (e.target.value) setTempTransportName("");
                }}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">— pick a master —</option>
                {(transportsQ.data?.items ?? [])
                  .filter((t) => t.is_active)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} — {t.vehicle_number}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">…or one-off vehicle</span>
              <input
                type="text"
                value={tempTransportName}
                onChange={(e) => {
                  setTempTransportName(e.target.value);
                  if (e.target.value) setTransportId("");
                }}
                placeholder="Temporary transport (won't auto-save to master)"
                maxLength={200}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              />
            </label>
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Anything driver / dock / customer needs to know."
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Deal</span>
              <select
                value={dealId}
                onChange={(e) => setDealId(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">- no deal -</option>
                {(dealsQ.data?.items ?? []).map((deal) => (
                  <option key={deal.id} value={deal.id}>
                    {deal.contact_summary?.name || deal.account_summary?.name || deal.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Account</span>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">- no account -</option>
                {(accountsQ.data?.items ?? []).map((account) => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium opacity-80">Contact</span>
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
              >
                <option value="">- no contact -</option>
                {(contactsQ.data?.items ?? []).map((contact) => (
                  <option key={contact.id} value={contact.id}>{contact.name}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-[11px] opacity-60">
            Links are added with the draft and recorded in the linked CRM activity.
          </p>
        </section>

        <section className="space-y-3 rounded-xl border border-[var(--border)] p-4">
          <header className="space-y-1">
            <h2 className="text-sm font-semibold">Deliveries</h2>
            <p className="text-xs opacity-60">
              Optional at create time. You can also save the draft now and add deliveries on the
              detail page.
            </p>
          </header>
          <DispatchDeliveriesEditor
            deliveries={deliveries}
            onChange={setDeliveries}
            warehouses={warehousesQ.data?.items ?? []}
            products={productsQ.data?.items ?? []}
            rawMaterials={rawsQ.data?.items ?? []}
            accounts={accountsQ.data?.items ?? []}
            contacts={contactsQ.data?.items ?? []}
            orders={dispatchableOrders}
          />
        </section>

        {error && (
          <p className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200">
            <AlertCircle className="size-3" aria-hidden />
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Link
            to="/erp/dispatches"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden />
            Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Create dispatch
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Light client-side validation. Real validation happens on the server
 * — we just want to catch the obvious "you forgot to pick X" cases
 * before submitting and surfacing a 422.
 */
function validateDraftDeliveries(deliveries: DraftDelivery[]): string | null {
  for (const [i, d] of deliveries.entries()) {
    const label = `Delivery #${i + 1}`;
    if (!d.source_warehouse_id) return `${label}: pick a source warehouse.`;
    if (d.kind === "internal") {
      if (!d.dest_warehouse_id) {
        return `${label}: internal deliveries need a destination warehouse.`;
      }
      if (d.dest_warehouse_id === d.source_warehouse_id) {
        return `${label}: source and destination warehouses must differ.`;
      }
    }
    if (d.kind === "external" && !d.account_id && !d.contact_id) {
      return `${label}: external deliveries need at least one of account / contact.`;
    }
    for (const [j, ln] of d.lines.entries()) {
      const lLabel = `${label}, line #${j + 1}`;
      if (ln.item_kind === "custom") {
        if (!ln.custom_name?.trim()) return `${lLabel}: custom item needs a name.`;
      } else if (!ln.item_id) {
        return `${lLabel}: pick a product or raw material.`;
      }
      if (!ln.qty.trim() || Number(ln.qty) <= 0) {
        return `${lLabel}: qty must be positive.`;
      }
      if (d.kind === "external") {
        if (!ln.unit_price.trim()) {
          return `${lLabel}: external deliveries require a unit price.`;
        }
        if (Number(ln.unit_price) < 0) {
          return `${lLabel}: unit price must be ≥ 0.`;
        }
      }
    }
  }
  return null;
}
